import { useCallback, useEffect, useState } from "react";

import Address from "@mapmycustomers/shared/types/Address";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";

import { AddressPickDetails, AddressPresets } from "./AddressPresets";
import usePlacePredictionsGetter from "./usePlacePredictionsGetter";
import usePlaceDetailsGetter from "./usePlacesService";

type AutocompletePrediction = google.maps.places.AutocompletePrediction;

const usePlaceSearch = ({
  isPrivateQuery,
  onChange,
  placePredictionTypes,
  value,
}: {
  isPrivateQuery?: AddressPresets["isPrivateQuery"];
  onChange?: (address?: Address, details?: AddressPickDetails) => void;
  placePredictionTypes?: string[];
  value?: Address;
}) => {
  const [placeList, setPlacesList] = useState<AutocompletePrediction[] | undefined>();
  const [placeListLoading, startLoading, stopLoading] = useBoolean();

  // Text field state + search listener to find suggestions
  const [getPlacePredictions] = usePlacePredictionsGetter(placePredictionTypes);
  // Resolves no list for private text: no search ran, which differs from one that found nothing
  const placePredictionsGetter = useCallback(
    (text: string): Promise<AutocompletePrediction[] | undefined> =>
      isPrivateQuery?.(text) ? Promise.resolve(undefined) : getPlacePredictions(text),
    [getPlacePredictions, isPrivateQuery]
  );
  const [fieldValue, setFieldValue, setFieldValueWithoutSearch] = useStateWithDebouncedListener(
    [
      useCallback(
        async (value: string) => {
          // No search runs for private text, which differs from a search that found nothing
          if (!value.trim() || isPrivateQuery?.(value)) {
            setPlacesList(undefined);
            return;
          }
          try {
            startLoading();
            const places = await placePredictionsGetter(value);
            setPlacesList(places);
          } finally {
            stopLoading();
          }
        },
        [isPrivateQuery, placePredictionsGetter, startLoading, stopLoading]
      ),
      100,
    ],
    ""
  );
  useEffect(() => {
    setFieldValueWithoutSearch(getFormattedAddressForUi(value));
  }, [setFieldValueWithoutSearch, value]);

  // Get address by place
  const [placeDetailsGetter] = usePlaceDetailsGetter();
  const selectPlace = useCallback(
    async (placeId: string, name?: string) => {
      const address = await placeDetailsGetter(placeId);
      setFieldValueWithoutSearch(getFormattedAddressForUi(address));
      onChange?.(address, { name, placeId });
    },
    [onChange, placeDetailsGetter, setFieldValueWithoutSearch]
  );

  return {
    fieldValue,
    placeList,
    placeListLoading,
    placePredictionsGetter,
    selectPlace,
    setFieldValue,
    setFieldValueWithoutSearch,
    setPlacesList,
  };
};

export default usePlaceSearch;
